import { useState, useEffect, useRef } from "react";
import { Mail, X, ChevronDown } from "lucide-react";
// import colors from "../ColorConfig/colorconfig";
import "./getintouch.css";
import { useNavigate } from "react-router-dom";
import useTheme from "../ColorConfig/usetheme";
import SEO from "../../AASeo/Seo";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

export default function GetInTouchForm({
  onClose,
  isFooterVersion = false,
  isContactVersion = false,
}) {
  const colors = useTheme();
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [services, setServices] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8923_Idlbay_Services_ServicesList"
        );
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    service: "",
    message: "",
    readstatus: "false",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleServiceSelect = (service) => {
    setFormData((prevState) => ({
      ...prevState,
      service: service.title,
    }));
    setSearchTerm(service.title);
    setIsDropdownOpen(false);
  };

  const handleServiceInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setFormData((prevState) => ({
      ...prevState,
      service: value,
    }));
    setIsDropdownOpen(true);
  };

  const filteredServices = services.filter((service) =>
    service.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (formSubmitted) return;

    // Prepare data with current datetime
    const submissionData = {
      ...formData,
      datetime: new Date().toLocaleString(),
    };

    try {
      if (isFooterVersion) {
        // Submit to footer version API
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8923_Idlbay_Messages_MessagesList",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              message: formData.message,
              datetime: new Date().toLocaleString(),
              readstatus: "false",
            }),
          }
        );

        if (response.ok) {
          console.log("Footer form submitted successfully");
          setFormSubmitted(true);
          navigate("/thankyou");
          // Reset form data for footer version
          setFormData({
            name: "",
            email: "",
            mobile: "",
            service: "",
            message: "",
            readstatus: "false",
          });
          // Reset submission status after a short delay
          setTimeout(() => setFormSubmitted(false), 2000);
        } else {
          console.error("Failed to submit footer form");
          setFormSubmitted(false);
        }
      } else {
        // Submit to modal version API
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8923_Idlbay_GetintouchForm_GetintouchFormMessages",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(submissionData),
          }
        );

        if (response.ok) {
          console.log("Modal form submitted successfully");
          setFormSubmitted(true);
          // Navigate to thank you page for modal version only
          navigate("/thankyou");
        } else {
          console.error("Failed to submit modal form");
          setFormSubmitted(false);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormSubmitted(false);
    }
  };

  // Helper function to determine if mobile view
  const isMobile = windowWidth <= 768;

  // If this is the footer version, use simplified styling
  if (isFooterVersion) {
    return (
      <form onSubmit={handleSubmit} className="idlbayfooter-form">
        <div className="idlbay-modal-form-header">
          <h3 className="idlbay-modal-form-title1">Let's Connect</h3>
        </div>

        <div className="idlbayfooter-form-field">
          <textarea
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleChange}
            rows="2"
            className="idlbayfooter-form-textarea"
            required
          />
        </div>
        <div className="idlbayfooter-form-msgbutton">
          <button
            type="submit"
            className="idlbayfooter-form-button"
            disabled={formSubmitted}
            style={{
              backgroundColor: `var(--color-primary) `,
              color: colors.white,
            }}
          >
            {formSubmitted ? "Sending..." : "Send Message"}
          </button>
        </div>
      </form>
    );
  }

  // Contact version - wider form with light background
  if (isContactVersion) {
    return (
      <>
        <SEO
          title="Contact Us | IDLBAY - Get In Touch"
          description="Contact IDLBAY for IT solutions, software development, web and mobile apps, and digital services. We're here to help your business grow."
          keywords="Contact IDLBAY, get in touch, IT solutions, software development, web development, mobile apps, digital services"
          url="https://idlbay.com/contactus"
          organization={organization}
        />
        <div className="idlbay-contact-form-container">
          {/* Form Content */}
          <div className="idlbay-contact-form-content">
            <form onSubmit={handleSubmit}>
              <div className="idlbay-contact-form-grid">
                <div className="idlbay-contact-form-field">
                  <label
                    className="idlbay-contact-form-label"
                    style={{ color: colors.white }}
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Aravind"
                    value={formData.name}
                    onChange={handleChange}
                    className="idlbay-contact-form-input"
                    required
                  />
                </div>

                <div className="idlbay-contact-form-field">
                  <label
                    className="idlbay-contact-form-label"
                    style={{ color: colors.white }}
                  >
                    Your Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="idlbay@gmail.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="idlbay-contact-form-input"
                    required
                  />
                </div>
              </div>

              <div className="idlbay-contact-form-grid">
                <div className="idlbay-contact-form-field">
                  <label
                    className="idlbay-contact-form-label"
                    style={{ color: colors.white }}
                  >
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="+91 9840 841 840"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="idlbay-contact-form-input"
                    required
                  />
                </div>

                <div className="idlbay-contact-form-field" ref={dropdownRef}>
                  <label
                    className="idlbay-contact-form-label"
                    style={{ color: colors.white }}
                  >
                    Select Service *
                  </label>
                  <div className="idlbay-contact-service-dropdown-container">
                    <input
                      type="text"
                      name="service"
                      placeholder="Type or select a service"
                      value={searchTerm}
                      onChange={handleServiceInputChange}
                      onFocus={() => setIsDropdownOpen(true)}
                      className="idlbay-contact-form-input idlbay-contact-service-input"
                      required
                    />
                    <ChevronDown
                      size={16}
                      className={`idlbay-contact-dropdown-icon ${
                        isDropdownOpen ? "rotated" : ""
                      }`}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    />

                    {isDropdownOpen && (
                      <div className="idlbay-contact-service-dropdown">
                        {filteredServices.length > 0 ? (
                          filteredServices.map((service, index) => (
                            <div
                              key={index}
                              className="idlbay-contact-service-option"
                              onClick={() => handleServiceSelect(service)}
                            >
                              {service.title}
                            </div>
                          ))
                        ) : (
                          <div className="idlbay-contact-service-option idlbay-contact-no-options">
                            No services found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="idlbay-contact-form-field">
                <label
                  className="idlbay-contact-form-label"
                  style={{ color: colors.white }}
                >
                  Message
                </label>
                <textarea
                  name="message"
                  placeholder="Write here your message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="idlbay-contact-form-textarea"
                />
              </div>

              <button
                type="submit"
                className="idlbay-contact-form-submit-btn"
                disabled={formSubmitted}
                style={{
                  backgroundColor: `var(--color-primary) `,
                  color: colors.white,
                }}
              >
                {formSubmitted ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  // Original modal version
  return (
    <div className="idlbay-modal-form-container">
      {/* Close button for modal */}
      {onClose && (
        <button onClick={onClose} className="idlbay-modal-close-btn1">
          <X size={18} />
        </button>
      )}

      {/* Main Container */}
      <div className="idlbay-modal-form-wrapper">
        {/* Form Content */}
        <div className="idlbay-modal-form-content">
          <div className="idlbay-modal-form-header">
            <div
              className="idlbay-modal-form-icon"
              style={{
                backgroundColor: `var(--color-primary) `,
                color: colors.white,
              }}
            >
              <Mail size={16} color={colors.white} />
            </div>
            <h3 className="idlbay-modal-form-title">Get In Touch</h3>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="idlbay-modal-form-grid">
              <div className="idlbay-modal-form-field">
                <label className="idlbay-modal-form-label">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Aravind"
                  value={formData.name}
                  onChange={handleChange}
                  className="idlbay-modal-form-input"
                  required
                />
              </div>

              <div className="idlbay-modal-form-field">
                <label className="idlbay-modal-form-label">Your Email *</label>
                <input
                  type="email"
                  name="email"
                  placeholder="idlbay@gmail.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="idlbay-modal-form-input"
                  required
                />
              </div>
            </div>

            <div className="idlbay-modal-form-grid">
              <div className="idlbay-modal-form-field">
                <label className="idlbay-modal-form-label">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="+91 9840 841 840"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="idlbay-modal-form-input"
                  required
                />
              </div>

              <div className="idlbay-modal-form-field" ref={dropdownRef}>
                <label className="idlbay-modal-form-label">
                  Select Service *
                </label>
                <div className="idlbay-service-dropdown-container">
                  <input
                    type="text"
                    name="service"
                    placeholder="Type or select a service"
                    value={searchTerm}
                    onChange={handleServiceInputChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="idlbay-modal-form-input idlbay-service-input"
                    required
                  />
                  <ChevronDown
                    size={16}
                    className={`idlbay-dropdown-icon ${
                      isDropdownOpen ? "rotated" : ""
                    }`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  />

                  {isDropdownOpen && (
                    <div className="idlbay-service-dropdown">
                      {filteredServices.length > 0 ? (
                        filteredServices.map((service, index) => (
                          <div
                            key={index}
                            className="idlbay-service-option"
                            onClick={() => handleServiceSelect(service)}
                          >
                            {service.title}
                          </div>
                        ))
                      ) : (
                        <div className="idlbay-service-option idlbay-no-options">
                          No services found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="idlbay-modal-form-field">
              <label className="idlbay-modal-form-label">Message</label>
              <textarea
                name="message"
                placeholder="Write here your message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
                className="idlbay-modal-form-textarea"
              />
            </div>

            <button
              type="submit"
              className="idlbay-modal-form-submit-btn"
              disabled={formSubmitted}
              style={{
                backgroundColor: `var(--color-primary) `,
                color: colors.white,
              }}
            >
              {formSubmitted ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
